import { useMemo } from 'react'
import { useQuery, useQueryClient, type QueryClient } from 'react-query'
import { CUSTOMER_LOOKUP, OWNER_LOOKUP, REAL_STATE_LOOKUP, fetchByIds, normalizeIds, retryUnlessClientError, type LookupSource } from '@/lib/lookups'
import type { CustomerType, Owner, RealStateDetail } from '@/lib/types'

const ENTITY_FRESH_MS = 60_000

type Identified = { id: string }

export interface EntityLookup<T> {
  byId: Map<string, T>
  isLoading: boolean
  isError: boolean
}

const entityKey = (source: LookupSource, id: string) => ['lookup-entity', source.key, id]

export function primeLookup<T extends Identified>(queryClient: QueryClient, source: LookupSource, entities: Array<T | null | undefined>) {
  for (const entity of entities) if (entity?.id) queryClient.setQueryData(entityKey(source, entity.id), entity)
}

function freshFromCache<T>(queryClient: QueryClient, source: LookupSource, id: string, now: number): T | undefined {
  const state = queryClient.getQueryState<T>(entityKey(source, id))
  return state?.data && now - state.dataUpdatedAt < ENTITY_FRESH_MS ? state.data : undefined
}

function useEntityLookup<T extends Identified>(source: LookupSource, ids: Iterable<string | null | undefined>): EntityLookup<T> {
  const queryClient = useQueryClient()
  const signature = normalizeIds(ids).join(',')
  const keyIds = useMemo(() => (signature ? signature.split(',') : []), [signature])

  const query = useQuery(
    ['lookup', source.key, keyIds],
    async () => {
      const now = Date.now()
      const cached: T[] = []
      const missing: string[] = []
      for (const id of keyIds) {
        const hit = freshFromCache<T>(queryClient, source, id, now)
        if (hit) cached.push(hit)
        else missing.push(id)
      }
      const fetched = missing.length > 0 ? await fetchByIds<T>(source, missing) : []
      primeLookup(queryClient, source, fetched)
      return [...cached, ...fetched]
    },
    { enabled: keyIds.length > 0, keepPreviousData: true, staleTime: ENTITY_FRESH_MS, refetchOnWindowFocus: false, retry: retryUnlessClientError }
  )

  const byId = useMemo(() => new Map((query.data ?? []).map(entity => [entity.id, entity])), [query.data])
  const isLoading = keyIds.length > 0 && query.isLoading
  const { isError } = query

  return useMemo(() => ({ byId, isLoading, isError }), [byId, isLoading, isError])
}

export const useCustomersByIds = (ids: Iterable<string | null | undefined>) => useEntityLookup<CustomerType>(CUSTOMER_LOOKUP, ids)

export const useRealStatesByIds = (ids: Iterable<string | null | undefined>) => useEntityLookup<RealStateDetail>(REAL_STATE_LOOKUP, ids)

export const useOwnersByIds = (ids: Iterable<string | null | undefined>) => useEntityLookup<Owner>(OWNER_LOOKUP, ids)
